import { useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { Upload, X, FileText } from "lucide-react";
import { useSnackbar } from "../../context/SnackbarContext";
import {
  uploadToStorage,
  generateStorageFilename,
} from "../../services/storageService";

const MAX_PDF_BYTES = 25 * 1024 * 1024;

export interface PdfUploadValue {
  url: string;
  fileName: string;
}

interface PdfUploadProps {
  /** Current file URL + display name, or null if no file. */
  value: PdfUploadValue | null;
  /** Called with the new value after upload, or null after delete. */
  onChange: (value: PdfUploadValue | null) => void;
  /** Storage path prefix, e.g. `events/{eventId}/sessions/{sessionId}/materials`. */
  storagePathPrefix: string;
  label?: string;
  disabled?: boolean;
  allowRemove?: boolean;
}

/**
 * PDF upload field for admin forms. Validates content type and size,
 * uploads to storage directly, captures the filename for display.
 *
 * Used for session materials. The 25 MB cap matches the storage rule
 * `pdfSizeOk()` constraint — server-side rules are the safety net but
 * we surface the limit at upload time for a better UX.
 */
export function PdfUpload({
  value,
  onChange,
  storagePathPrefix,
  label = "PDF document",
  disabled = false,
  allowRemove = true,
}: PdfUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const { showError } = useSnackbar();

  function openPicker() {
    inputRef.current?.click();
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    if (file.type !== "application/pdf") {
      showError("Please select a PDF file");
      return;
    }
    if (file.size > MAX_PDF_BYTES) {
      const mb = (file.size / 1024 / 1024).toFixed(1);
      showError(`PDF is too large (${mb} MB). Maximum 25 MB.`);
      return;
    }

    setUploading(true);
    try {
      const filename = generateStorageFilename("pdf");
      const path = `${storagePathPrefix.replace(/\/$/, "")}/${filename}`;
      const url = await uploadToStorage(path, file, "application/pdf");
      onChange({ url, fileName: file.name });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Upload failed";
      showError(`PDF upload failed: ${message}`);
    } finally {
      setUploading(false);
    }
  }

  function handleRemove() {
    onChange(null);
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        {/* File chip */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 1,
            backgroundColor: "background.default",
            minHeight: 48,
            minWidth: 240,
          }}
        >
          {uploading ? (
            <>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Uploading...
              </Typography>
            </>
          ) : value ? (
            <>
              <FileText size={18} />
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {value.fileName}
              </Typography>
              {allowRemove && !disabled && (
                <IconButton
                  size="small"
                  onClick={handleRemove}
                  aria-label="Remove PDF"
                >
                  <X size={14} />
                </IconButton>
              )}
            </>
          ) : (
            <>
              <FileText size={18} color="currentColor" />
              <Typography variant="body2" color="text.secondary">
                No file selected
              </Typography>
            </>
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Upload size={16} />}
            onClick={openPicker}
            disabled={disabled || uploading}
            data-testid="pdf-upload-button"
          >
            {value ? "Replace" : "Upload PDF"}
          </Button>
          <Typography variant="caption" color="text.secondary">
            PDF only, max 25 MB.
          </Typography>
        </Box>
      </Box>

      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />
    </Box>
  );
}
